import React, { useState, useEffect, useMemo } from 'react';
import ShopFilters from './ShopFilters';
import ShopHeader from './ShopHeader';
import GridView from './GridView';
import ListView from './ListView';
import Pagination from './Pagination';
import { getShopPreferences, saveViewMode, saveItemsPerPage, saveSortBy } from '../../utils/localStorage';
import { useShop } from '../../context/ShopContext';

function ShopArea() {
  // Context
  const { products: allProducts, collections, loading, error, refreshShop } = useShop();

  // Initialize state with localStorage preferences
  const preferences = getShopPreferences();

  const [viewMode, setViewMode] = useState(preferences.viewMode);

  // Refresh data when visiting the shop to ensure freshness
  useEffect(() => {
    refreshShop();
  }, [refreshShop]);
  const [selectedColors, setSelectedColors] = useState({});
  const [activeCollectionFilter, setActiveCollectionFilter] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 150 });
  const [tempPriceRange, setTempPriceRange] = useState({ min: 0, max: 150 });
  const [itemsPerPage, setItemsPerPage] = useState(Math.max(9, preferences.itemsPerPage));
  const [sortBy, setSortBy] = useState(preferences.sortBy);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [preloaderFadeOut, setPreloaderFadeOut] = useState(false);

  // Save preferences
  useEffect(() => { saveViewMode(viewMode); }, [viewMode]);
  useEffect(() => { saveItemsPerPage(itemsPerPage); }, [itemsPerPage]);
  useEffect(() => { saveSortBy(sortBy); }, [sortBy]);

  // Handle preloader fade out
  useEffect(() => {
    if (!loading) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setPreloaderFadeOut(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setPreloaderFadeOut(false);
    }
  }, [loading]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCollectionFilter, priceRange, searchQuery, sortBy]);

  // Filter and Sort Products
  const filteredProducts = useMemo(() => {
    if (!allProducts) return [];

    let result = [...allProducts];

    // 1. Search
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(lowerQuery) ||
        (p.collection && p.collection.name && p.collection.name.toLowerCase().includes(lowerQuery))
      );
    }

    // 2. Collection Filter
    if (activeCollectionFilter.length > 0) {
      result = result.filter(p => activeCollectionFilter.includes(p.collectionId));
    }

    // 3. Price Filter
    result = result.filter(p => {
      // Price format handling "123.00 TND"
      let pPrice = p.price;
      if (typeof pPrice === 'string') {
        pPrice = parseFloat(pPrice.replace(' TND', '').trim());
      }
      return pPrice >= priceRange.min && pPrice <= priceRange.max;
    });

    // 4. Sort
    result.sort((a, b) => {
      let priceA = a.price;
      let priceB = b.price;

      if (typeof priceA === 'string') priceA = parseFloat(priceA.replace(' TND', '').trim());
      if (typeof priceB === 'string') priceB = parseFloat(priceB.replace(' TND', '').trim());

      // Fallback to originalPrice if parsing fails or if we want to use base price
      if (isNaN(priceA)) priceA = a.originalPrice || 0;
      if (isNaN(priceB)) priceB = b.originalPrice || 0;

      switch (sortBy) {
        case 'Low to high': return priceA - priceB;
        case 'High to low': return priceB - priceA;
        case 'Popularity': return (b.popularity || 0) - (a.popularity || 0);
        default: return 0; // Newest / Default
      }
    });

    return result;
  }, [allProducts, searchQuery, activeCollectionFilter, priceRange, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  // Handlers
  const handleSearch = (query) => setSearchQuery(query);

  const handleCollectionFilter = (collectionId) => {
    setActiveCollectionFilter(prev => {
      if (Array.isArray(prev)) {
        return prev.includes(collectionId) ? prev.filter(id => id !== collectionId) : [...prev, collectionId];
      }
      return prev === collectionId ? [] : [collectionId];
    });
  };

  const handlePriceRangeChange = (type, value) => {
    const numValue = parseFloat(value) || 0;
    setTempPriceRange(prev => {
      const newRange = { ...prev, [type]: numValue };
      if (type === 'min' && numValue > prev.max) newRange.max = numValue;
      if (type === 'max' && numValue < prev.min) newRange.min = numValue;
      return newRange;
    });
  };

  const handleApplyPriceRange = () => setPriceRange(tempPriceRange);
  const handleResetPriceRange = () => {
    const reset = { min: 0, max: 150 };
    setTempPriceRange(reset);
    setPriceRange(reset);
  };

  const handleColorSelect = (productId, colorId) => {
    setSelectedColors(prev => ({ ...prev, [productId]: colorId }));
  };

  const getSelectedColor = (productId) => {
    return selectedColors[productId] || allProducts.find(p => p.id === productId)?.defaultColor || null;
  };

  const getImageForProduct = (product) => {
    const selectedColorId = getSelectedColor(product.id);
    if (!product.colors || product.colors.length === 0) return product.img || '';
    const selectedColor = product.colors.find(c => c.id === selectedColorId);
    return selectedColor?.imagePath || product.img || '';
  };

  // Render Error
  if (error) {
    return (
      <div className="shop-area pt-110 pb-110">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-12 text-center">
              <div className="alert alert-danger">
                <h4>Erreur de chargement des produits</h4>
                <p>{error}</p>
                <button className="btn btn-primary mt-3" onClick={refreshShop}>Réessayer</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-area pt-110 pb-110">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-3 col-md-8 order-2 order-lg-0">
            <ShopFilters
              activeCollectionFilter={activeCollectionFilter}
              collections={collections}
              priceRange={tempPriceRange}
              handlePriceRangeChange={handlePriceRangeChange}
              handleCollectionFilter={handleCollectionFilter}
              setActiveCollectionFilter={setActiveCollectionFilter}
              setPriceRange={setPriceRange} // Pass setter even if redundant to match original props if needed
              handleApplyPriceRange={handleApplyPriceRange}
              handleResetPriceRange={handleResetPriceRange}
              onSearch={handleSearch}
            />
          </div>
          <div className="col-lg-9">
            <ShopHeader
              viewMode={viewMode}
              setViewMode={setViewMode}
              filteredProducts={filteredProducts}
              products={filteredProducts}
              activeCollectionFilter={activeCollectionFilter}
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              sortBy={sortBy}
              setSortBy={setSortBy}
              currentPage={currentPage}
              totalPages={totalPages}
            />

            <div className="products-container" style={{ position: 'relative', minHeight: '200px' }}>
              {filteredProducts.length === 0 && !loading ? (
                <div className="row">
                  <div className="col-lg-12 text-center">
                    <div className="alert alert-info">
                      <h4>Aucun produit trouvé</h4>
                      <p>Aucun produit n'est disponible pour le moment.</p>
                      <p className="text-muted small">Essayez d'ajuster vos filtres.</p>
                    </div>
                  </div>
                </div>
              ) : viewMode === 'grid' ? (
                <GridView
                  products={paginatedProducts}
                  selectedColors={selectedColors}
                  handleColorSelect={handleColorSelect}
                  getSelectedColor={getSelectedColor}
                  getImageForProduct={getImageForProduct}
                  collections={collections}
                />
              ) : (
                <ListView
                  products={paginatedProducts}
                  selectedColors={selectedColors}
                  handleColorSelect={handleColorSelect}
                  getSelectedColor={getSelectedColor}
                  getImageForProduct={getImageForProduct}
                  collections={collections}
                />
              )}

              {loading && (
                <div className={`shop-preloader-overlay ${preloaderFadeOut ? 'fade-out' : 'shop-preloader-loading'}`}>
                  <img src="img/preloader.gif" alt="Loading..." className="preloader-gif" />
                </div>
              )}
            </div>

            {filteredProducts.length > 0 && (
              <Pagination
                viewMode={viewMode}
                setViewMode={setViewMode}
                filteredProducts={filteredProducts}
                products={filteredProducts}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                sortBy={sortBy}
                setSortBy={setSortBy}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShopArea;